/**
 * Hippo Featured Products — horizontal slider controls.
 *
 * The row of `.fp-card`s lives in a native scroll-snap flex rail (`.grid--uniform`,
 * styled in featured-collection-hippo.css). This script wires the desktop prev/next
 * arrows to page-scroll that rail and keeps them in sync with scroll position:
 *
 *   - click prev/next → scrollBy one "page" (~90% of the visible width), smooth
 *   - on scroll / resize → disable prev at the start, next at the end
 *   - no overflow (all cards fit) → hide the nav entirely (data-slider-overflow)
 *
 * Scoped per `data-section-id`, so multiple featured-collection sections on one page
 * work independently. Re-inits on Shopify/theme section-load so it survives the theme
 * editor. The card markup and its own hover controls are never touched here.
 */
;(function () {
  'use strict'

  const SECTION_SELECTOR = '.featured-collection--hippo[data-section-id]'
  const reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches

  /**
   * @param {HTMLElement} root - the .featured-collection--hippo section wrapper
   */
  function initSection(root) {
    if (!root || root.dataset.sliderInit === 'true') return

    const track = /** @type {HTMLElement | null} */ (root.querySelector('.grid--uniform'))
    const prev = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-slider-prev]'))
    const next = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-slider-next]'))
    if (!track || !prev || !next) return

    root.dataset.sliderInit = 'true'

    /** @param {number} dir - -1 for previous, 1 for next */
    const page = (dir) => {
      track.scrollBy({
        left: dir * track.clientWidth * 0.9,
        behavior: reduceMotion ? 'auto' : 'smooth',
      })
    }

    prev.addEventListener('click', () => page(-1))
    next.addEventListener('click', () => page(1))

    let rafId = 0
    const update = () => {
      rafId = 0
      const max = track.scrollWidth - track.clientWidth
      const overflow = max > 1
      root.dataset.sliderOverflow = overflow ? 'true' : 'false'
      prev.disabled = track.scrollLeft <= 0
      next.disabled = track.scrollLeft >= max - 1
    }
    const schedule = () => {
      if (!rafId) rafId = window.requestAnimationFrame(update)
    }

    track.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    window.addEventListener('load', schedule)

    update()
  }

  const initAll = () => {
    document
      .querySelectorAll(SECTION_SELECTOR)
      .forEach((el) => initSection(/** @type {HTMLElement} */ (el)))
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll)
  } else {
    initAll()
  }

  /** @param {Event} e */
  const onSectionLoad = (e) => {
    const target = /** @type {HTMLElement} */ (e.target)
    if (!target || !target.querySelector) return
    const root =
      target.matches && target.matches(SECTION_SELECTOR)
        ? target
        : target.querySelector(SECTION_SELECTOR)
    if (root) initSection(/** @type {HTMLElement} */ (root))
  }
  document.addEventListener('shopify:section:load', onSectionLoad)
  document.addEventListener('theme:section:load', onSectionLoad)
})()
